import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { isRemembered, remember } from '../utils/affiliateAuth';
import AdminNav from './AdminNav';
import './SentEmails.css';

// The recruiting outreach log, served from the same app as /affiliates.
//
// Data comes from the recruiting server (Hetzner, reads recruiting.sqlite
// directly) through the /recruit Pages Function proxy, which injects that
// server's basic-auth credentials. So this component just calls same-origin
// URLs — no CORS, no second password for the recruiting box.
const RECRUIT_API = '/recruit/api';

// Shares the overview's password namespace: unlock /affiliates and this page
// stops asking too (see utils/affiliateAuth).
const OVERVIEW_PASSWORD = '0987';

// Per-email state for the action column, from /api/draft-status: which emails
// have a reply draft waiting in Gmail, which are blocked, which were invited.
interface DraftState {
  has: Set<string>;
  blocked: Set<string>;
  invited: Set<string>;
}

// Result of an invite/block click, shown in place of the buttons.
type ActionState = { kind: 'pending' | 'done' | 'error'; text: string };

// Focused-review session: the frozen queue plus where we are in it. The queue is
// snapshotted on open so rows settling underneath (a poll landing, a filter
// changing) can't shuffle the deck mid-review.
interface ReviewState {
  queue: SentRow[];
  idx: number;
  invited: number;
  blocked: number;
  busy: boolean;
  note: { kind: 'pending' | 'done' | 'error'; text: string } | null;
}

interface SentRow {
  // Absent on the DB-reply rows of /review-queue: those come from `creators`,
  // not `sent_emails_log`, so they have no sent-log id to key off.
  id?: number;
  email: string;
  username: string;
  sent_at: string;
  subject: string;
  message_id: string | null;
  region: string | null;
  outreach_status: string | null;
  reply_classification: string | null;
  reply_snippet: string | null;
  ethnicity: string | null;
  age: string | null;
  found_keyword: string | null;
  // Present only on the DB-reply rows of /review-queue (the ones without a Gmail
  // draft): they carry the discovery keyword as an array instead of found_keyword.
  found_via?: { keyword: string; date: string }[] | null;
  gender: string | null;
  follower_count: number | null;
  // Self-reported on the apply form. Joined in by the sent-log queries; still
  // filled from /apply-queue as a fallback for rows that predate that join.
  form_country?: string | null;
  form_language?: string | null;
  // 2026-10-12: apply 폼으로만 지원한 사람(메일 답장 없음). /apply-queue 에서 와서 리뷰 목록에 합쳐진다.
  from_apply?: boolean;
  applied_at?: string | null;
}

// Profile photo saved at discovery, served by the recruit API (TikTok's own avatar URLs are signed,
// expire and come as heic). Hidden when there is none.
const TikTokAvatar: React.FC<{ username: string }> = ({ username }) => {
  const [hidden, setHidden] = useState(false);
  useEffect(() => { setHidden(false); }, [username]);
  if (hidden) return null;
  return (
    <img
      className="sent-emails__tt-avatar"
      src={`${RECRUIT_API}/avatar/${encodeURIComponent(username)}`}
      alt=""
      onError={() => setHidden(true)}
    />
  );
};

// TikTok profile screenshot on R2; without one, the saved avatar and a note. Never TikTok's
// embed: it answers "overload-protect triggered" almost every time (removed 2026-10-29).
const TIKTOK_SHOT_BASE = 'https://pub-a7ae2ad40d864e0cacb490384e74670f.r2.dev/dstribute/tiktok-shots';
const TikTokShot: React.FC<{ username: string; note: string }> = ({ username, note }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [username]);
  if (!failed) {
    return (
      <div className="sent-emails__review-shot">
        <img
          key={username}
          src={`${TIKTOK_SHOT_BASE}/${encodeURIComponent(username)}.jpg`}
          alt={`@${username} TikTok profile`}
          onError={() => setFailed(true)}
        />
      </div>
    );
  }
  return (
    <div className="sent-emails__review-shot sent-emails__tt">
      <div className="sent-emails__tt-head">
        <TikTokAvatar username={username} />
        <div className="sent-emails__tt-name">@{username}</div>
      </div>
      <p className="sent-emails__tt-note">{note}</p>
    </div>
  );
};

// Profile card from EnsembleData, prepared on the server for ICP rows
// (ssemble-marketing scripts/prepTiktokProfiles.js → R2 tiktok-profiles/{username}.json, read via the
// recruit API because r2.dev sends no CORS headers). Falls back to the screenshot/embed above when
// the JSON is missing or unavailable.
interface TikTokProfileDoc {
  status: 'ok' | 'unavailable';
  nickname?: string;
  bio?: string;
  followers?: number | null;
  likes?: number | null;
  videoCount?: number | null;
  fetchedAt?: string;
  videos?: { id: string; cover: string | null; views: number | null; createdAt: string | null; pinned: boolean }[];
}
const fmtCount = (n: number | null | undefined): string => {
  if (n == null) return '–';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
};
const daysAgo = (iso: string | null): string => {
  if (!iso) return '';
  const d = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  return d <= 0 ? 'today' : `${d}d`;
};
const TikTokProfile: React.FC<{ username: string }> = ({ username }) => {
  const [doc, setDoc] = useState<TikTokProfileDoc | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setDoc(undefined);
    fetch(`${RECRUIT_API}/tiktok-profile/${encodeURIComponent(username)}`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((j) => { if (alive) setDoc(j); });
    return () => { alive = false; };
  }, [username]);
  if (doc === undefined) return <div className="sent-emails__review-shot" />;
  if (!doc || doc.status !== 'ok') {
    const note = doc
      ? 'TikTok returned no profile for this account (private, deleted or renamed).'
      : 'Profile not prepared yet. The server fetches new ones every 20 minutes.';
    return <TikTokShot username={username} note={note} />;
  }
  return (
    <div className="sent-emails__review-shot sent-emails__tt">
      <div className="sent-emails__tt-head">
        <TikTokAvatar username={username} />
        <div>
          <div className="sent-emails__tt-name">{doc.nickname || username} <span>@{username}</span></div>
          <div className="sent-emails__tt-stats">
            <b>{fmtCount(doc.followers)}</b> followers · <b>{fmtCount(doc.likes)}</b> likes · <b>{fmtCount(doc.videoCount)}</b> videos
          </div>
          {doc.bio && <div className="sent-emails__tt-bio">{doc.bio}</div>}
        </div>
      </div>
      <div className="sent-emails__tt-grid">
        {(doc.videos || []).map((v) => (
          <a key={v.id} href={`https://www.tiktok.com/@${encodeURIComponent(username)}/video/${v.id}`} target="_blank" rel="noreferrer">
            {v.cover ? <img src={v.cover} alt="" loading="lazy" /> : <div className="sent-emails__tt-nocover" />}
            {v.pinned && <span className="sent-emails__tt-pin">Pinned</span>}
            <span className="sent-emails__tt-views">▶ {fmtCount(v.views)} · {daysAgo(v.createdAt)}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

const flagEmoji = (code: string | null): string => {
  const cc = (code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map(c => 0x1f1e6 + (c.charCodeAt(0) - 65)));
};

// Demographic labels, mirroring the recruiting dashboard's sent log so the two
// surfaces read the same. English here (this app is English throughout).
const ETHNICITY_LABEL: Record<string, string> = {
  white: 'White', black: 'Black', hispanic: 'Hispanic', east_asian: 'East Asian',
  northeast_asian: 'NE Asian', southeast_asian: 'SE Asian', south_asian: 'South Asian',
  arab: 'Arab', unknown: '?',
};
const AGE_LABEL: Record<string, string> = {
  teens: 'teens', '20s': '20s', '30s': '30s', '40plus': '40+', unknown: '?',
};

// Country cell: our detected region (TikTok) and, when the creator applied and
// self-reported a different country, both — detected → self-reported, same as the
// recruiting dashboard. form_country only exists on apply-queue rows, so it is
// passed in separately rather than read off the sent row.
const COUNTRY_CODE: Record<string, string> = {
  'United States': 'US', 'United Kingdom': 'GB', Canada: 'CA', Australia: 'AU',
  'New Zealand': 'NZ', Ireland: 'IE', 'South Africa': 'ZA',
  Argentina: 'AR',
  Spain: 'ES',
};
const countryCode = (name: string): string => COUNTRY_CODE[name] || name;

const fmtFollowers = (n: number | null): string => {
  if (!n && n !== 0) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, '')}K`;
  return String(n);
};

// Replies worth a human's attention, in the order we want them surfaced.
const WARM = ['positive', 'question', 'negotiate'];

// ICP = the six English-speaking countries discovery was narrowed to on 2026-09-07
// (WINNER-YIELD-2026-09.md). The review queue is built from "has a pending draft /
// replied", which has no country condition, so it still surfaces people contacted
// back when discovery spanned 24 countries. Reviewing those is wasted time — they
// are not who we recruit any more.
const ICP_COUNTRIES = new Set(['US', 'GB', 'CA', 'AU', 'IE', 'NZ', 'AR', 'ES']); // AR 10/18 · ES 10/19 허용(ssemble-marketing reviewIcp.js와 동일)

// Unknown region stays IN the queue: a blank region is missing data, not a foreign
// creator, and silently dropping those would hide real US/GB people.
const isIcp = (row: { region: string | null; form_country?: string | null }): boolean => {
  const raw = (row.form_country || row.region || '').trim();
  if (!raw) return true;
  return ICP_COUNTRIES.has(countryCode(raw).toUpperCase());
};

// One day of pipeline activity, from /api/stats-timeseries (KST buckets).
interface DayPoint {
  bucket: string;      // YYYY-MM-DD
  searches: number;
  new_accounts: number;
  emails: number;
}

// One email in the outreach sequence, from /api/email-templates.
interface EmailTemplate {
  step: number;
  label: string;
  when: string;
  subject: string;
  body: string;
}

// /api/followup-config — whether the no-reply follow-up sender is actually running.
// /api/keyword-prompt — how the discovery loop is generating search keywords right now:
// the last Claude prompt it sent (verbatim) and the last round's source mix.
interface KeywordPrompt {
  round: number;
  at: string;
  composition: Record<string, number>;
  keywords: string[];
  text: string;
  // 시드 계보(2026-09-24): 이 시드들에서 자가증식을 다시 시작했다. seed_epoch = 시작 시각. lineage = epoch 이후 누적 성과.
  seeds?: string[];
  seed_epoch?: string | null;
  lineage?: { searches: number; units: number; new_creators: number; icp: number; sends: number } | null;
  lineage_strict?: boolean;  // true=시드 계보만(엄격), false=전역 풀 자가증식 + 시드 선호(2026-09-24 유저 결정)
}

// /api/keyword-recent — 최근 검색한 키워드 N개와 그 검색의 성과(search_log 한 행 = 검색 1건).
interface RecentRun {
  id: number;
  keyword: string;
  date: string;
  source: string | null;      // seed | claude | hashtag | variant | stale | retry | auto | manual
  period_days: number | null;
  videos_fetched: number | null;
  fetched: number | null;      // 유니크 저자
  with_email: number | null;   // 6개국·팔로워 상한 통과 + 바이오 이메일
  matched: number | null;      // 인구통계 ICP(검색 시점)
  new: number | null;          // DB에 처음 들어온 사람
  units_used: number | null;
  sends: number;               // 그 검색이 찾은 사람 중 발송된 수(성숙까지 ~30분)
  icp_email: number;
}

interface FollowupStatus {
  enabled: boolean;
  sent_today: number;
  remaining: number;
  max_followups: number;
  day_offsets: number[];
  consecutive_failures: number;
  last_error: string | null;
  last_error_at: string | null;
  auto_paused: boolean;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// The chart covers a fixed trailing window rather than one request per month: the
// series is one small row per day, so fetching the lot once makes month-stepping
// instant and costs a single request.
const TIMESERIES_DAYS = 400;

const replyLabel = (c: string | null): { text: string; tone: string } | null => {
  if (!c) return null;
  const map: Record<string, { text: string; tone: string }> = {
    positive: { text: 'positive', tone: 'good' },
    question: { text: 'question', tone: 'good' },
    negotiate: { text: 'negotiate', tone: 'good' },
    rejected: { text: 'rejected', tone: 'bad' },
    negative: { text: 'negative', tone: 'bad' },
    slack_joined: { text: 'joined', tone: 'accent' },
    unclassified_reply: { text: 'reply', tone: 'neutral' },
    other: { text: 'other', tone: 'neutral' },
  };
  return map[c] || { text: c, tone: 'neutral' };
};

const fmtDate = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16).replace('T', ' ');
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const SentEmails: React.FC = () => {
  const [authed, setAuthed] = useState(() => isRemembered('overview', OVERVIEW_PASSWORD));
  const [password, setPassword] = useState('');
  const [rows, setRows] = useState<SentRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [warmOnly, setWarmOnly] = useState(true);
  // Action layer: draft/blocked/invited state per email, plus per-row results
  // and the reply-preview modal.
  const [draft, setDraft] = useState<DraftState>({ has: new Set(), blocked: new Set(), invited: new Set() });
  const [applied, setApplied] = useState<Set<string>>(new Set());
  // Form answers (self-reported country + language) keyed by email. These live
  // only on /apply-queue — the sent-log endpoints don't join them — so the two
  // extra demographic columns are filled from here for creators who applied.
  const [formInfo, setFormInfo] = useState<Record<string, { country: string; language: string }>>({});
  const [actions, setActions] = useState<Record<string, ActionState>>({});
  const [preview, setPreview] = useState<{ email: string; body: string } | null>(null);
  // Focused review: walk the queue one creator at a time with their TikTok
  // profile on screen, so the call is made on the profile rather than the row.
  const [review, setReview] = useState<ReviewState | null>(null);
  const [reviewReply, setReviewReply] = useState<string | null>(null);
  // Background invite/block failures. Surfaced in the review bar so a detached
  // request can't fail silently while the reviewer has already moved on.
  const [failures, setFailures] = useState<{ email: string; username: string; kind: 'invite' | 'block'; error: string }[]>([]);
  // On by default: the queue predates the 6-country narrowing, so most of the
  // non-ICP rows in it are people we would no longer contact at all.
  const [icpOnly, setIcpOnly] = useState(true);
  // Below-the-table panels: the daily activity chart, the email sequence, and
  // whether the follow-up sender is actually running.
  const [series, setSeries] = useState<DayPoint[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [openStep, setOpenStep] = useState<number>(0);
  const [followup, setFollowup] = useState<FollowupStatus | null>(null);
  const [kwPrompt, setKwPrompt] = useState<KeywordPrompt | null>(null);
  const [recentRuns, setRecentRuns] = useState<RecentRun[]>([]);
  // 최근 키워드 10개는 1분마다 갱신(발송은 검색 뒤 ~30분에 걸쳐 붙는다).
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${RECRUIT_API}/keyword-recent?limit=10`);
        if (r.ok) { const d = await r.json(); if (!cancelled) setRecentRuns(Array.isArray(d.runs) ? d.runs : []); }
      } catch { /* panel stays empty */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [authed]);
  const [resuming, setResuming] = useState(false);
  const now = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1); // 1-12

  const load = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      // An empty query returns nothing from /api/sent-search (it filters on the
      // term), so the default view asks the review queue for what needs a human.
      const url = q.trim()
        ? `${RECRUIT_API}/sent-search?limit=500&q=${encodeURIComponent(q.trim())}`
        : `${RECRUIT_API}/review-queue`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const list: SentRow[] = d.sent_emails_log || d.rows || [];
      // 2026-10-12: 리뷰 목록이 메일 답장자(/review-queue)로만 만들어져, apply 폼으로만 지원한
      // 남성 ~1,900명이 리뷰에 한 번도 안 떴다. 기본 화면에선 /apply-queue(서버가 이미 미초대·미거절·
      // ICP로 거르고 최신 지원순 정렬)를 답장자 뒤에 붙인다. 이미 목록에 있는 사람(답장도 한 지원자)은 중복 제외.
      if (!q.trim()) {
        try {
          const ra = await fetch(`${RECRUIT_API}/apply-queue`);
          if (ra.ok) {
            const da = await ra.json();
            type ApplyRow = { email: string; username: string; region?: string | null; form_country?: string | null; form_language?: string | null; applied_at?: string | null; ethnicity?: string | null; age?: string | null; gender?: string | null; follower_count?: number | null; found_via?: { keyword: string; date: string }[] | null };
            const seen = new Set(list.map(x => (x.email || '').toLowerCase()));
            for (const a of (da.rows || []) as ApplyRow[]) {
              const e = (a.email || '').toLowerCase();
              if (!e || !a.username || seen.has(e)) continue;
              seen.add(e);
              list.push({
                email: a.email, username: a.username, sent_at: a.applied_at || '', subject: '', message_id: null,
                region: a.region ?? null, outreach_status: 'applied', reply_classification: null, reply_snippet: null,
                ethnicity: a.ethnicity ?? null, age: a.age ?? null,
                found_keyword: (a.found_via && a.found_via[0] && a.found_via[0].keyword) || null, found_via: a.found_via ?? null,
                gender: a.gender ?? null, follower_count: a.follower_count ?? null,
                form_country: a.form_country ?? null, form_language: a.form_language ?? null,
                from_apply: true, applied_at: a.applied_at ?? null,
              });
            }
          }
        } catch { /* 폼 지원자 합치기 실패해도 답장자 리뷰는 그대로 */ }
      }
      setRows(list);
      setTotal(typeof d.total === 'number' ? d.total : list.length);
      // Searching means "find this person", so don't keep hiding most of the
      // results behind the review-queue filter; clearing the search restores it.
      setWarmOnly(!q.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authed) load(''); }, [authed, load]);

  // Panel data: fetched once on unlock. Each is independent of the table's
  // search/filter state, and a failure leaves that panel empty rather than
  // breaking the log above it.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${RECRUIT_API}/stats-timeseries?bucket=day&limit=${TIMESERIES_DAYS}`);
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setSeries(d.series || []);
        }
      } catch { /* chart stays empty */ }
      try {
        const r = await fetch(`${RECRUIT_API}/email-templates`);
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setTemplates(d.templates || []);
        }
      } catch { /* sequence stays empty */ }
      try {
        const r = await fetch(`${RECRUIT_API}/followup-config`);
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setFollowup(d.status || null);
        }
      } catch { /* status stays empty */ }
      try {
        const r = await fetch(`${RECRUIT_API}/keyword-prompt`);
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setKwPrompt(d && d.text ? d : null);
        }
      } catch { /* prompt panel stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [authed]);

  // Which of the rows on screen have a draft / are blocked / were invited, and
  // which came in through the apply form. Both feed the action column: a row is
  // actionable if it has a reply draft, applied via the form, or replied warmly.
  useEffect(() => {
    if (!authed || rows.length === 0) return;
    let cancelled = false;
    (async () => {
      // 폼 지원자(from_apply)는 제외: draft-status 는 이메일마다 Gmail 검색을 하므로 ~2,000명을 넣으면
      // 다시 100초를 넘는다. 이들은 서버가 이미 미초대·미거절로 걸렀고 답장 초안도 없다.
      const emails = [...new Set(rows.filter(r => !r.from_apply).map(r => r.email).filter(Boolean))];
      try {
        // Chunked: the upstream builds a Gmail query per email, and a few hundred
        // at once overruns its URL length.
        const next: DraftState = { has: new Set(), blocked: new Set(), invited: new Set() };
        for (let i = 0; i < emails.length; i += 40) {
          const chunk = emails.slice(i, i + 40);
          const r = await fetch(`${RECRUIT_API}/draft-status?emails=${encodeURIComponent(chunk.join(','))}`);
          if (!r.ok) break;
          const d = await r.json();
          (d.has || []).forEach((e: string) => next.has.add(e));
          (d.blocked || []).forEach((e: string) => next.blocked.add(e));
          (d.invited || []).forEach((e: string) => next.invited.add(e));
        }
        if (!cancelled) setDraft(next);
      } catch { /* leave the column empty rather than blocking the table */ }
      try {
        const r = await fetch(`${RECRUIT_API}/apply-queue`);
        if (r.ok) {
          const d = await r.json();
          type ApplyRow = { email: string; form_country?: string | null; form_language?: string | null };
          const list: ApplyRow[] = d.rows || [];
          if (!cancelled) {
            setApplied(new Set(list.map(x => x.email).filter(Boolean)));
            const info: Record<string, { country: string; language: string }> = {};
            for (const x of list) {
              if (!x.email) continue;
              info[x.email.toLowerCase()] = { country: x.form_country || '', language: x.form_language || '' };
            }
            setFormInfo(info);
          }
        }
      } catch { /* same */ }
    })();
    return () => { cancelled = true; };
  }, [authed, rows]);

  const invite = useCallback(async (email: string) => {
    if (!window.confirm(`Send this creator the drafted reply and invite them to Slack?\n\n${email}`)) return;
    setActions(a => ({ ...a, [email]: { kind: 'pending', text: 'Sending…' } }));
    try {
      const r = await fetch(`${RECRUIT_API}/invite-send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'failed');
      // Slack outcomes: ok = already a member and DM'd now; will_dm_on_join =
      // invited, the poller DMs when they join (the normal case, not a failure);
      // skipped = Slack not configured.
      const sl = d.slack || {};
      const note = sl.skipped ? ' (Slack off)'
        : sl.ok ? ' + Slack DM'
        : sl.will_dm_on_join ? ' + Slack invite'
        : ` (Slack: ${(sl.dm && sl.dm.error) || sl.error || '?'})`;
      setActions(a => ({ ...a, [email]: { kind: 'done', text: `✓ Invited${note}` } }));
    } catch (e) {
      setActions(a => ({ ...a, [email]: { kind: 'error', text: e instanceof Error ? e.message.slice(0, 40) : 'failed' } }));
    }
  }, []);

  const block = useCallback(async (email: string) => {
    if (!window.confirm(
      `Block this email?\n\n• deletes every draft to them\n• excludes them from discovery, sending and invites\n• future mail from them is auto-trashed in Gmail\n\n${email}`
    )) return;
    setActions(a => ({ ...a, [email]: { kind: 'pending', text: 'Blocking…' } }));
    try {
      const r = await fetch(`${RECRUIT_API}/invite-block`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'failed');
      const gm = d.trashFilter ? ' + Gmail filter' : '';
      setActions(a => ({ ...a, [email]: { kind: 'done', text: `⛔ Blocked (${d.draftsDeleted || 0} drafts${gm})` } }));
    } catch (e) {
      setActions(a => ({ ...a, [email]: { kind: 'error', text: e instanceof Error ? e.message.slice(0, 40) : 'failed' } }));
    }
  }, []);

  // Clear the follow-up sender's failure latch. It pauses after N consecutive
  // failures and never retries on its own, so it stays down until something
  // resets it — even once the underlying problem is gone.
  const resumeFollowups = useCallback(async () => {
    if (!window.confirm(
      'Resume follow-up sending?\n\nThis clears the paused state and the sender starts working through its queue again. Make sure whatever caused the failures is actually fixed.'
    )) return;
    setResuming(true);
    try {
      const r = await fetch(`${RECRUIT_API}/followup-resume`, { method: 'POST' });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'failed');
      setFollowup(d.status || null);
    } catch (e) {
      window.alert(`Couldn't resume: ${e instanceof Error ? e.message : 'failed'}`);
    } finally {
      setResuming(false);
    }
  }, []);

  const showReply = useCallback(async (email: string) => {
    setPreview({ email, body: 'Loading…' });
    try {
      const r = await fetch(`${RECRUIT_API}/reply-preview?email=${encodeURIComponent(email)}`);
      const d = await r.json();
      setPreview({
        email,
        body: d.configured === false ? 'Gmail is not connected.' : ((d.body || '').trim() || "Couldn't find the reply body."),
      });
    } catch (e) {
      setPreview({ email, body: e instanceof Error ? `Error: ${e.message}` : 'Error' });
    }
  }, []);

  const visible = useMemo(() => {
    if (!warmOnly) return rows;
    return rows.filter(r => r.from_apply || (r.reply_classification && WARM.includes(r.reply_classification)));
  }, [rows, warmOnly]);

  /* ── Focused review ──────────────────────────────────────────────────────
     Same invite/block endpoints as the table, minus the per-row confirm: the
     profile is on screen and the decision has already been made by the time
     the button is hit, so a dialog on every card would defeat the point. A
     failure stops the queue in place instead of advancing past it. */

  // Reviewable = still awaiting a decision. Anything already invited, blocked
  // or acted on in this session drops out, so re-opening resumes where it left off.
  const pendingDecision = useMemo(
    () => visible.filter(r => {
      const e = r.email.toLowerCase();
      return r.username && !draft.blocked.has(e) && !draft.invited.has(e)
        && actions[r.email]?.kind !== 'done';
    }),
    [visible, draft, actions],
  );

  const reviewable = useMemo(
    () => (icpOnly ? pendingDecision.filter(isIcp) : pendingDecision),
    [pendingDecision, icpOnly],
  );

  // How many the ICP filter is holding back — shown on the toggle so the skipped
  // ones stay visible as a number rather than just vanishing.
  const nonIcpCount = pendingDecision.length - pendingDecision.filter(isIcp).length;

  const startReview = useCallback(() => {
    if (!reviewable.length) return;
    setReviewReply(null);
    setFailures([]);
    setReview({ queue: reviewable, idx: 0, invited: 0, blocked: 0, busy: false, note: null });
  }, [reviewable]);

  const reviewGo = useCallback((delta: number) => {
    setReviewReply(null);
    setReview(s => (s ? { ...s, idx: Math.max(0, s.idx + delta), note: null } : s));
  }, []);

  /* Advance immediately, send in the background.
     Waiting on the round-trip before showing the next creator makes the queue feel
     like a form; the decision is already made by the time the button is hit, so the
     network has no reason to be in the way. The request fires detached and its
     outcome lands on the table row (and the failure list) whenever it returns.
     Failures are surfaced in the top bar instead of blocking — see `failures`. */
  const reviewAct = useCallback((kind: 'invite' | 'block') => {
    let row: SentRow | undefined;
    setReview(s => {
      if (!s) return s;
      row = s.queue[s.idx];
      if (!row) return s;
      // Move on at once — no busy state, no waiting.
      return {
        ...s,
        idx: s.idx + 1,
        invited: s.invited + (kind === 'invite' ? 1 : 0),
        blocked: s.blocked + (kind === 'block' ? 1 : 0),
        busy: false,
        note: null,
      };
    });
    if (!row) return;
    const email = row.email;
    const username = row.username;
    setReviewReply(null);
    // Optimistic: the row shows the intent right away, corrected if the call fails.
    setActions(a => ({
      ...a,
      [email]: { kind: 'pending', text: kind === 'invite' ? 'Inviting…' : 'Blocking…' },
    }));
    setDraft(prev => {
      const next = { has: new Set(prev.has), blocked: new Set(prev.blocked), invited: new Set(prev.invited) };
      (kind === 'invite' ? next.invited : next.blocked).add(email.toLowerCase());
      return next;
    });
    void (async () => {
      try {
        const r = await fetch(`${RECRUIT_API}/${kind === 'invite' ? 'invite-send' : 'invite-block'}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
        });
        const d = await r.json();
        if (!d.ok) throw new Error(d.error || 'failed');
        setActions(a => ({
          ...a,
          [email]: { kind: 'done', text: kind === 'invite' ? '✓ Invited' : `⛔ Blocked (${d.draftsDeleted || 0} drafts)` },
        }));
      } catch (e) {
        const msg = e instanceof Error ? e.message.slice(0, 60) : 'failed';
        setActions(a => ({ ...a, [email]: { kind: 'error', text: `✗ ${msg}` } }));
        // Roll back the optimistic membership so the row doesn't look handled.
        setDraft(prev => {
          const next = { has: new Set(prev.has), blocked: new Set(prev.blocked), invited: new Set(prev.invited) };
          (kind === 'invite' ? next.invited : next.blocked).delete(email.toLowerCase());
          return next;
        });
        // A background failure must not vanish: name it in the bar so it can be retried.
        setFailures(f => [...f, { email, username, kind, error: msg }]);
      }
    })();
  }, []);

  const showReviewReply = useCallback(async (email: string) => {
    setReviewReply('Loading…');
    try {
      const r = await fetch(`${RECRUIT_API}/reply-preview?email=${encodeURIComponent(email)}`);
      const d = await r.json();
      setReviewReply(d.configured === false
        ? 'Gmail is not connected.'
        : ((d.body || '').trim() || "Couldn't find the reply body."));
    } catch (e) {
      setReviewReply(e instanceof Error ? `Error: ${e.message}` : 'Error');
    }
  }, []);

  // Keyboard: ← → to move, I/B to decide, Esc to leave.
  useEffect(() => {
    if (!review) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const done = review.idx >= review.queue.length;
      if (e.key === 'Escape') { setReview(null); return; }
      if (done) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); reviewGo(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); reviewGo(-1); }
      else if (e.key.toLowerCase() === 'i') { e.preventDefault(); void reviewAct('invite'); }
      else if (e.key.toLowerCase() === 'b') { e.preventDefault(); void reviewAct('block'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [review, reviewGo, reviewAct]);

  // The chart shows one month at a time. Every day of the month gets a point,
  // including days with no activity, so a gap reads as a real stop rather than
  // the line skipping over it.
  const monthData = useMemo(() => {
    const prefix = `${viewYear}-${String(viewMonth).padStart(2, '0')}`;
    const byDay = new Map<number, DayPoint>();
    for (const p of series) {
      if (p.bucket && p.bucket.startsWith(prefix)) byDay.set(Number(p.bucket.slice(-2)), p);
    }
    const days = new Date(viewYear, viewMonth, 0).getDate();
    return Array.from({ length: days }, (_, i) => {
      const day = i + 1;
      const p = byDay.get(day);
      return {
        day,
        searches: p?.searches ?? 0,
        new_accounts: p?.new_accounts ?? 0,
        emails: p?.emails ?? 0,
      };
    });
  }, [series, viewYear, viewMonth]);

  const monthTotals = useMemo(() => monthData.reduce(
    (a, d) => ({
      searches: a.searches + d.searches,
      new_accounts: a.new_accounts + d.new_accounts,
      emails: a.emails + d.emails,
    }),
    { searches: 0, new_accounts: 0, emails: 0 }
  ), [monthData]);

  // Months we actually have data for — stepping past either end is pointless.
  const monthBounds = useMemo(() => {
    const keys = series.map(p => p.bucket?.slice(0, 7)).filter(Boolean).sort();
    return { first: keys[0] || null, last: keys[keys.length - 1] || null };
  }, [series]);
  const viewKey = `${viewYear}-${String(viewMonth).padStart(2, '0')}`;
  const atFirstMonth = !monthBounds.first || viewKey <= monthBounds.first;
  const atLastMonth = !monthBounds.last || viewKey >= monthBounds.last;

  const goPrevMonth = useCallback(() => {
    setViewMonth(m => (m === 1 ? 12 : m - 1));
    setViewYear(y => (viewMonth === 1 ? y - 1 : y));
  }, [viewMonth]);

  const goNextMonth = useCallback(() => {
    setViewMonth(m => (m === 12 ? 1 : m + 1));
    setViewYear(y => (viewMonth === 12 ? y + 1 : y));
  }, [viewMonth]);

  const warmCount = useMemo(
    () => rows.filter(r => r.from_apply || (r.reply_classification && WARM.includes(r.reply_classification))).length,
    [rows]
  );

  if (!authed) {
    return (
      <div className="sent-emails">
        <div className="sent-emails__login">
          <h1 className="sent-emails__login-title">Sent emails</h1>
          <input
            className="sent-emails__input"
            type="password"
            value={password}
            placeholder="Password"
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && password === OVERVIEW_PASSWORD) {
                remember('overview', password); setAuthed(true);
              }
            }}
          />
          <button
            className="sent-emails__btn"
            onClick={() => {
              if (password === OVERVIEW_PASSWORD) { remember('overview', password); setAuthed(true); }
            }}
          >Enter</button>
        </div>
      </div>
    );
  }

  // The action column mirrors the old dashboard's rules: blocked and invited are
  // terminal states; otherwise a row is actionable when it has a reply draft,
  // came through the apply form, or replied warmly (those last two have no Gmail
  // draft but are exactly who we want to invite).
  const renderAction = (r: SentRow) => {
    const act = actions[r.email];
    if (act) {
      const tone = act.kind === 'error' ? 'bad' : act.kind === 'done' ? 'good' : 'neutral';
      return <span className={`sent-emails__result sent-emails__result--${tone}`}>{act.text}</span>;
    }
    if (draft.blocked.has(r.email)) return <span className="sent-emails__muted">⛔ blocked</span>;
    if (draft.invited.has(r.email)) return <span className="sent-emails__result sent-emails__result--good">✓ invited</span>;

    const warm = r.reply_classification && WARM.includes(r.reply_classification);
    const actionable = draft.has.has(r.email) || applied.has(r.email) || warm;
    if (!actionable) return <span className="sent-emails__muted">—</span>;

    return (
      <span className="sent-emails__action-btns">
        <button className="sent-emails__act sent-emails__act--invite" onClick={() => invite(r.email)}>✉ Invite</button>
        <button className="sent-emails__act sent-emails__act--block" onClick={() => block(r.email)}>⛔ Block</button>
        {r.reply_classification && (
          <button className="sent-emails__act sent-emails__act--ghost" onClick={() => showReply(r.email)}>👁</button>
        )}
      </span>
    );
  };

  return (
    <div className="sent-emails">
      <div className="sent-emails__container">
        <div className="sent-emails__header">
          <div>
            <h1 className="sent-emails__title">📧 Sent emails</h1>
            <p className="sent-emails__subtitle">
              Cold outreach log from the recruiting pipeline
              {total !== null && (
                <> · showing <strong>{visible.length.toLocaleString()}</strong>
                  {total > visible.length && <> of <strong>{total.toLocaleString()}</strong></>}
                </>
              )}
            </p>
          </div>
          <AdminNav />
        </div>

        <div className="sent-emails__controls">
          <input
            className="sent-emails__search"
            type="text"
            value={query}
            placeholder="🔍 Search the full log — username · email · subject"
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load(query); }}
          />
          <button className="sent-emails__btn" onClick={() => load(query)} disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>
          {query && (
            <button className="sent-emails__btn sent-emails__btn--ghost" onClick={() => { setQuery(''); load(''); }}>
              Clear
            </button>
          )}
          <label className="sent-emails__filter">
            <input type="checkbox" checked={warmOnly} onChange={e => setWarmOnly(e.target.checked)} />
            Needs a reply only
            {warmCount > 0 && <span className="sent-emails__count">{warmCount}</span>}
          </label>
          <label
            className="sent-emails__filter"
            title="Only US · GB · CA · AU · IE · NZ — the countries discovery targets. Unknown-country rows are kept."
          >
            <input type="checkbox" checked={icpOnly} onChange={e => setIcpOnly(e.target.checked)} />
            🌐 ICP only
            {nonIcpCount > 0 && <span className="sent-emails__count">−{nonIcpCount}</span>}
          </label>
          <button
            className="sent-emails__btn sent-emails__btn--review"
            onClick={startReview}
            disabled={!reviewable.length}
            title={reviewable.length ? 'Review each creator on their profile' : 'Nothing left to review'}
          >
            ▶ Review {reviewable.length > 0 && `(${reviewable.length})`}
          </button>
        </div>

        {error && <p className="sent-emails__error">Couldn't load the log: {error}</p>}

        <div className="sent-emails__table-wrap">
          <table className="sent-emails__table">
            <thead>
              <tr>
                <th>Creator</th>
                <th>Country</th>
                <th>Ethnicity</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Followers</th>
                <th>Language</th>
                <th>Email</th>
                <th>Sent</th>
                <th>Reply</th>
                <th>Keyword</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const rep = replyLabel(r.reply_classification);
                const fallback = formInfo[(r.email || '').toLowerCase()];
                const form = {
                  country: r.form_country || fallback?.country || '',
                  language: r.form_language || fallback?.language || '',
                };
                // Draft-backed rows carry found_keyword; DB-reply rows carry found_via
                // instead, so fall back to it or half the keyword cells read "—".
                const keyword = r.found_keyword || (r.found_via && r.found_via[0] && r.found_via[0].keyword) || '';
                const selfCode = form.country ? countryCode(form.country) : '';
                const detected = (r.region || '').trim().toUpperCase();
                const mismatch = !!(detected && selfCode && selfCode.toUpperCase() !== detected);
                return (
                  <tr key={r.id ?? `db-${r.email}`}>
                    <td>
                      <a
                        className="sent-emails__handle"
                        href={`https://www.tiktok.com/@${r.username}`}
                        target="_blank"
                        rel="noreferrer"
                      >@{r.username}</a>
                    </td>
                    <td className="sent-emails__nowrap">
                      {detected || selfCode ? (
                        <span title={mismatch ? `detected ${detected} · applied ${form.country}` : undefined}>
                          {detected && <>{flagEmoji(detected)} {detected}</>}
                          {mismatch && <> <span className="sent-emails__muted">→</span> {flagEmoji(selfCode)} {selfCode}</>}
                          {!detected && selfCode && <>{flagEmoji(selfCode)} {selfCode}</>}
                        </span>
                      ) : <span className="sent-emails__muted">—</span>}
                    </td>
                    <td className="sent-emails__nowrap">
                      {r.ethnicity
                        ? (ETHNICITY_LABEL[r.ethnicity] || r.ethnicity)
                        : <span className="sent-emails__muted">—</span>}
                    </td>
                    <td className="sent-emails__nowrap">
                      {r.age ? (AGE_LABEL[r.age] || r.age) : <span className="sent-emails__muted">—</span>}
                    </td>
                    <td className="sent-emails__nowrap">
                      {r.gender || <span className="sent-emails__muted">—</span>}
                    </td>
                    <td className="sent-emails__nowrap">
                      {r.follower_count || r.follower_count === 0
                        ? fmtFollowers(r.follower_count)
                        : <span className="sent-emails__muted">—</span>}
                    </td>
                    <td className="sent-emails__nowrap">
                      {form.language || <span className="sent-emails__muted">—</span>}
                    </td>
                    <td className="sent-emails__email">{r.email}</td>
                    <td className="sent-emails__nowrap">{fmtDate(r.sent_at)}</td>
                    <td>
                      {rep
                        ? <span className={`sent-emails__tag sent-emails__tag--${rep.tone}`} title={r.reply_snippet || ''}>{rep.text}</span>
                        : <span className="sent-emails__muted">—</span>}
                    </td>
                    <td className="sent-emails__keyword">{keyword || <span className="sent-emails__muted">—</span>}</td>
                    <td className="sent-emails__actions">{renderAction(r)}</td>
                  </tr>
                );
              })}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={12} className="sent-emails__empty">
                    {warmOnly && rows.length > 0
                      ? 'No replies waiting — untick "Needs a reply only" to see the rest.'
                      : query ? 'Nothing matched that search.' : 'Nothing waiting for a reply right now.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Daily pipeline activity, one month at a time. Searches and new accounts
            dwarf email sends, so sends get their own right-hand axis — on a shared
            axis the email line sits flat on the floor and tells you nothing. */}
        <section className="sent-emails__panel">
          <div className="sent-emails__panel-head">
            <h2 className="sent-emails__panel-title">📈 Daily activity</h2>
            <div className="sent-emails__month-nav">
              <button
                type="button"
                className="sent-emails__month-arrow"
                onClick={goPrevMonth}
                disabled={atFirstMonth}
                aria-label="Previous month"
              >‹</button>
              <span className="sent-emails__month-label">{MONTH_NAMES[viewMonth - 1]} {viewYear}</span>
              <button
                type="button"
                className="sent-emails__month-arrow"
                onClick={goNextMonth}
                disabled={atLastMonth}
                aria-label="Next month"
              >›</button>
            </div>
          </div>
          <p className="sent-emails__panel-sub">
            {monthTotals.searches.toLocaleString()} searches ·{' '}
            {monthTotals.new_accounts.toLocaleString()} creators found ·{' '}
            {monthTotals.emails.toLocaleString()} emails sent this month
          </p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthData} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} tickLine={false} axisLine={{ stroke: 'var(--border-primary)' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} tickLine={false} axisLine={false} width={46} tickFormatter={(v: number) => v >= 1000 ? `${Math.round(v / 1000)}K` : String(v)} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#2E7D32' }} tickLine={false} axisLine={false} width={44} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)' }}
                formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                labelFormatter={(d) => `${MONTH_NAMES[viewMonth - 1]} ${d}, ${viewYear}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="searches" name="Searches" stroke="#b08bd9" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line yAxisId="left" type="monotone" dataKey="new_accounts" name="Creators found" stroke="#5b7fc7" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line yAxisId="right" type="monotone" dataKey="emails" name="Emails sent" stroke="#2E7D32" strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </section>

        {/* What we actually send: the first-touch email and the three no-reply
            follow-ups, straight from the sender's own templates. */}
        {templates.length > 0 && (
          <section className="sent-emails__panel">
            <div className="sent-emails__panel-head">
              <h2 className="sent-emails__panel-title">✉️ The email sequence</h2>
              {followup && (
                <span className={`sent-emails__pill sent-emails__pill--${followup.auto_paused ? 'bad' : followup.enabled ? 'good' : 'neutral'}`}>
                  {followup.auto_paused ? 'Follow-ups paused' : followup.enabled ? 'Follow-ups on' : 'Follow-ups off'}
                </span>
              )}
            </div>

            {/* A paused sender is the thing you most need to know here: the queue
                keeps filling while nothing goes out. */}
            {followup?.auto_paused && (
              <p className="sent-emails__alert">
                Auto-paused after {followup.consecutive_failures} consecutive failures
                {followup.last_error_at && <> since {fmtDate(followup.last_error_at)}</>}.
                {followup.remaining > 0 && <> {followup.remaining.toLocaleString()} creators are waiting on a follow-up.</>}
                {followup.last_error && <><br /><code className="sent-emails__code">{followup.last_error.trim().slice(0, 200)}</code></>}
                <br />
                <button
                  type="button"
                  className="sent-emails__resume"
                  onClick={resumeFollowups}
                  disabled={resuming}
                >{resuming ? 'Resuming…' : '▶ Resume follow-ups'}</button>
              </p>
            )}
            {followup && !followup.auto_paused && (
              <p className="sent-emails__panel-sub">
                {followup.sent_today.toLocaleString()} follow-ups sent today ·{' '}
                {followup.remaining.toLocaleString()} creators waiting · up to {followup.max_followups} per creator
              </p>
            )}

            <div className="sent-emails__steps">
              {templates.map(t => (
                <button
                  key={t.step}
                  type="button"
                  className={`sent-emails__step${openStep === t.step ? ' sent-emails__step--on' : ''}`}
                  onClick={() => setOpenStep(t.step)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {templates.filter(t => t.step === openStep).map(t => (
              <div key={t.step} className="sent-emails__mail">
                <p className="sent-emails__mail-when">{t.when}</p>
                <p className="sent-emails__mail-subject"><strong>Subject:</strong> {t.subject}</p>
                <pre className="sent-emails__mail-body">{t.body}</pre>
              </div>
            ))}
          </section>
        )}

        {/* How the search keywords are chosen: the exact prompt the discovery loop
            sent to Claude in its last round, plus where that round's keywords came
            from (Claude proposals vs. the hashtag co-occurrence pool etc.). */}
        {kwPrompt && (
          <section className="sent-emails__panel">
            <div className="sent-emails__panel-head">
              <h2 className="sent-emails__panel-title">🔑 How keywords are generated</h2>
            </div>
            <p className="sent-emails__panel-sub">
              Round {kwPrompt.round.toLocaleString()} · {fmtDate(kwPrompt.at)}
              {Object.keys(kwPrompt.composition).length > 0 && (
                <> · {kwPrompt.keywords.length} keywords this round: {Object.entries(kwPrompt.composition).map(([k, v]) => `${k} ${v}`).join(' · ')}</>
              )}
            </p>
            {kwPrompt.seeds && kwPrompt.seeds.length > 0 && (
              <div className="sent-emails__seed-box">
                <div className="sent-emails__seed-title">🌱 Seed keywords</div>
                <div className="sent-emails__seed-list">
                  {kwPrompt.seeds.map(sd => <span key={sd} className="sent-emails__seed-chip">{sd}</span>)}
                </div>
                <div className="sent-emails__seed-meta">
                  Self-propagation restarted from these seeds{kwPrompt.seed_epoch ? ` on ${fmtDate(kwPrompt.seed_epoch)}` : ''}.
                  {kwPrompt.lineage_strict
                    ? ' Strict mode: only keywords searched since then feed the prompt, the hashtag pool and re-searches.'
                    : ' Mode: global pool — the seeds are re-searched daily (window × sort combos) and given preference in the prompt, but the hashtag pool, 7-day re-searches and stale wells come from the whole history (the seed-only lineage ran dry within 5 hours).'}
                  {kwPrompt.lineage && (
                    <> Lineage so far: {kwPrompt.lineage.searches.toLocaleString()} searches · {kwPrompt.lineage.new_creators.toLocaleString()} new creators · {kwPrompt.lineage.icp.toLocaleString()} ICP · {kwPrompt.lineage.sends.toLocaleString()} emails sent · {kwPrompt.lineage.units.toLocaleString()} units.</>
                  )}
                </div>
              </div>
            )}
            {recentRuns.length > 0 && (
              <div className="sent-emails__recent-kw">
                <div className="sent-emails__seed-title">🕒 Last {recentRuns.length} keywords searched</div>
                <table className="sent-emails__table sent-emails__table--compact">
                  <thead>
                    <tr>
                      <th>Keyword</th><th>Source</th><th>When</th>
                      <th title="유니크 저자 수">Authors</th>
                      <th title="6개국·팔로워 상한 통과 + 바이오 이메일">Email</th>
                      <th title="인구통계 ICP(검색 시점)">ICP</th>
                      <th title="DB에 처음 들어온 사람">New</th>
                      <th title="그 검색이 찾은 사람 중 발송된 수 — 검색 뒤 ~30분에 걸쳐 붙는다">Sent</th>
                      <th>Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map(r => (
                      <tr key={r.id}>
                        <td><strong>{r.keyword}</strong>{r.period_days && r.period_days !== 30 ? <span className="sent-emails__muted"> · {r.period_days}d</span> : null}</td>
                        <td>{r.source || '—'}</td>
                        <td>{fmtDate(r.date)}</td>
                        <td>{r.fetched ?? '—'}</td>
                        <td>{r.with_email ?? '—'}</td>
                        <td>{r.matched ?? '—'}</td>
                        <td>{r.new ?? '—'}</td>
                        <td><strong>{r.sends}</strong></td>
                        <td>{r.units_used ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="sent-emails__mail-when">
              Prompt sent to Claude for this round's proposals. Each round is filled to its size from
              the sources above in order — seeds first, then Claude, then the hashtag co-occurrence pool harvested
              from creators found in this lineage.
            </p>
            <pre className="sent-emails__mail-body">{kwPrompt.text}</pre>
          </section>
        )}
      </div>

      {review && (() => {
        const done = review.idx >= review.queue.length;
        const row = review.queue[review.idx];
        const total = review.queue.length;
        const pct = Math.round((Math.min(review.idx, total) / total) * 100);
        const profile = row ? `https://www.tiktok.com/@${encodeURIComponent(row.username)}` : '';
        // Emoji carry the row at a glance — in a fast queue the icon is read before
        // the label is. The country gets its real flag (same helper as the table);
        // gender picks its own glyph rather than sharing one for both.
        // Emoji sit on the VALUE, not the label — the label column stays a clean
        // left-aligned list, and the glyph reads as part of what it describes.
        // Country is ALWAYS shown, even unknown: in an ICP-filtered queue "which
        // country" is the thing you check first, and a missing row reads as "no
        // country requirement" rather than "we don't know". `always` forces it
        // past the empty-value filter below.
        const country = row ? (row.form_country || row.region) : null;
        const genderIcon = row?.gender === 'male' ? '👨' : row?.gender === 'female' ? '👩' : '🧑';
        const meta: { label: string; value: string | null; icon: string; always?: boolean }[] = row ? [
          { label: 'Email', value: row.email, icon: '✉️' },
          {
            label: 'Country',
            value: country || 'Unknown',
            icon: country ? (flagEmoji(countryCode(country)) || '🌍') : '❔',
            always: true,
          },
          { label: 'Followers', value: fmtFollowers(row.follower_count), icon: '👥' },
          { label: 'Ethnicity', value: row.ethnicity ? (ETHNICITY_LABEL[row.ethnicity] || row.ethnicity) : null, icon: '🧬' },
          { label: 'Age', value: row.age ? (AGE_LABEL[row.age] || row.age) : null, icon: '🎂' },
          { label: 'Gender', value: row.gender, icon: genderIcon },
          { label: 'Language', value: row.form_language || null, icon: '🗣️' },
          { label: 'Keyword', value: row.found_keyword || (row.found_via && row.found_via[0]?.keyword) || null, icon: '🔍' },
        ] : [];
        return (
          <div className="sent-emails__review">
            <div className="sent-emails__review-bar">
              <strong>🎬 Review</strong>
              <div className="sent-emails__review-track">
                <div className="sent-emails__review-fill" style={{ width: `${pct}%` }} />
              </div>
              {failures.length > 0 && (
                <span
                  className="sent-emails__review-fail"
                  title={failures.map(f => `@${f.username || f.email}: ${f.kind} — ${f.error}`).join('\n')}
                >
                  ⚠ {failures.length} failed
                </span>
              )}
              <span className="sent-emails__review-count">
                {done ? `${total} / ${total}` : `${review.idx + 1} / ${total}`}
              </span>
              <button className="sent-emails__modal-close" onClick={() => setReview(null)}>×</button>
            </div>

            {done ? (
              <div className="sent-emails__review-done">
                <div className="sent-emails__review-done-icon">🎉</div>
                <h3>Review complete</h3>
                <p>{review.invited} invited · {review.blocked} blocked</p>
                {failures.length > 0 && (
                  <div className="sent-emails__review-faillist">
                    <p>{failures.length} didn't go through — these are still unhandled:</p>
                    <ul>
                      {failures.map(f => (
                        <li key={f.email}>@{f.username || f.email} — {f.kind} failed: {f.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button className="sent-emails__btn" onClick={() => setReview(null)}>Close</button>
              </div>
            ) : (
              <div className="sent-emails__review-main">
                {/* The profile card prepared on the server (prepTiktokProfiles.js): saved avatar,
                    stats, bio and recent video covers. No TikTok embed — it is blocked with
                    "overload-protect triggered" almost every time. */}
                <div className="sent-emails__review-frame">
                  <TikTokProfile username={row.username} />
                  <a className="sent-emails__review-open" href={profile} target="_blank" rel="noopener noreferrer">
                    ↗ Open @{row.username} on TikTok
                  </a>
                </div>

                <div className="sent-emails__review-side">
                  <div className="sent-emails__review-info">
                    <h3>@{row.username}</h3>
                    <a href={profile} target="_blank" rel="noopener noreferrer">{profile}</a>
                    {row.reply_classification && (() => {
                      const L = replyLabel(row.reply_classification);
                      return L ? <span className={`sent-emails__tag sent-emails__tag--${L.tone} sent-emails__review-chip`}>{L.text}</span> : null;
                    })()}
                    {row.from_apply && !row.reply_classification && (
                      <span className="sent-emails__tag sent-emails__tag--accent sent-emails__review-chip">applied (form)</span>
                    )}
                    <dl className="sent-emails__review-meta">
                      {meta.filter(m => m.value || m.always).map(m => (
                        <div key={m.label}>
                          <dt>{m.label}</dt>
                          <dd className={m.value === 'Unknown' ? 'is-unknown' : undefined}>
                            <span className="sent-emails__review-ico" aria-hidden="true">{m.icon}</span>
                            {m.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    {row.reply_snippet && (
                      <button className="sent-emails__btn sent-emails__btn--ghost sent-emails__review-replybtn"
                        onClick={() => (reviewReply === null ? void showReviewReply(row.email) : setReviewReply(null))}>
                        💬 {reviewReply === null ? 'Show reply' : 'Hide reply'}
                      </button>
                    )}
                    {reviewReply !== null && <div className="sent-emails__review-replybody">{reviewReply}</div>}
                  </div>

                  <div className="sent-emails__review-actions">
                    {review.note && (
                      <div className={`sent-emails__review-note sent-emails__review-note--${review.note.kind}`}>
                        {review.note.text}
                      </div>
                    )}
                    <div className="sent-emails__review-row">
                      <button className="sent-emails__review-block" onClick={() => reviewAct('block')}>⛔ Block</button>
                      <button className="sent-emails__review-invite" onClick={() => reviewAct('invite')}>✉ Invite</button>
                    </div>
                    <div className="sent-emails__review-row">
                      <button className="sent-emails__btn sent-emails__btn--ghost" disabled={review.idx === 0} onClick={() => reviewGo(-1)}>← Prev</button>
                      <button className="sent-emails__btn sent-emails__btn--ghost" onClick={() => reviewGo(1)}>Skip →</button>
                    </div>
                    <p className="sent-emails__review-keys">
                      <kbd>←</kbd> <kbd>→</kbd> move · <kbd>I</kbd> invite · <kbd>B</kbd> block · <kbd>Esc</kbd> exit
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {preview && (
        <div className="sent-emails__modal" onClick={() => setPreview(null)}>
          <div className="sent-emails__modal-box" onClick={e => e.stopPropagation()}>
            <div className="sent-emails__modal-head">
              <h3>Reply — {preview.email}</h3>
              <button className="sent-emails__modal-close" onClick={() => setPreview(null)}>×</button>
            </div>
            <div className="sent-emails__modal-body">{preview.body}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentEmails;
